import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface DealCardProps {
  deal: {
    id: string;
    year: number;
    make: string;
    model: string;
    price: number;
    mileage: number;
    images: string[];
    dealType: string[];
    dealTemperature: 'hot' | 'trending' | 'cool';
    viewerCount: number;
    daysOnLot: number;
    priceDropAmount?: number;
    arrivalDate?: string;
    urgencyMessage?: string;
  };
}

export default function DealCard({ deal }: DealCardProps) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    if (deal.dealType.includes('last-chance')) {
      const calc = () => {
        const endDate = new Date(Date.now() + (45 - deal.daysOnLot) * 24 * 60 * 60 * 1000);
        const diff = endDate.getTime() - Date.now();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        setTimeLeft(`${days}d ${hours}h`);
      };
      calc();
      const interval = setInterval(calc, 60000);
      return () => clearInterval(interval);
    }
  }, [deal.daysOnLot, deal.dealType]);

  const imageUrl =
    deal.images[0] ||
    'https://public.readdy.ai/ai/img_res/a21b7d66e20e05011c9c557eca278f5b.jpg';

  const isLastChance = deal.dealType.includes('last-chance');
  const isPriceDrop = deal.dealType.includes('price-drops');
  const isJustArrived = deal.dealType.includes('just-arrived');

  // Single top-right badge — priority: price drop > just arrived > nothing
  const cornerBadge = isPriceDrop
    ? { label: `↓ $${deal.priceDropAmount?.toLocaleString()} OFF`, cls: 'bg-green-500 text-white' }
    : isJustArrived
    ? { label: 'JUST LANDED', cls: 'bg-[#1C2B1E] text-white' }
    : null;

  return (
    <div className="bg-white rounded-xl border border-[#E8E2D6] overflow-hidden flex flex-col h-full hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
      {/* Image */}
      <div className="relative h-48 flex-shrink-0 bg-gray-100 overflow-hidden">
        <img
          src={imageUrl}
          alt={`${deal.year} ${deal.make} ${deal.model}`}
          className="w-full h-full object-cover object-center"
          width={800}
          height={192}
          loading="lazy"
          decoding="async"
        />
        {/* Temperature badge — top left only */}
        {deal.dealTemperature === 'hot' && (
          <div className="absolute top-3 left-3 bg-red-500 text-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow">
            <i className="ri-fire-line text-xs" aria-hidden="true" />
            Moves Fast
          </div>
        )}
        {deal.dealTemperature === 'trending' && (
          <div className="absolute top-3 left-3 bg-orange-500 text-white px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow">
            <i className="ri-flashlight-line text-xs" aria-hidden="true" />
            Trending
          </div>
        )}
        {/* Corner badge — top right, one only */}
        {cornerBadge && (
          <div className={`absolute top-3 right-3 ${cornerBadge.cls} px-2 py-1 rounded text-xs font-bold shadow`}>
            {cornerBadge.label}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Title + mileage */}
        <div className="mb-3">
          <h3 className="font-['Oswald'] text-lg font-bold text-[#1C2B1E] leading-tight">
            {deal.year} {deal.make} {deal.model}
          </h3>
          <p className="text-sm text-[#1C2B1E]/50 mt-0.5">{deal.mileage.toLocaleString()} miles</p>
        </div>

        {/* Price row */}
        <div className="flex items-baseline justify-between mb-4">
          <span className="text-2xl font-black text-[#1C2B1E]">${deal.price.toLocaleString()}</span>
          {isPriceDrop && deal.priceDropAmount && (
            <span className="text-sm text-green-600 font-semibold">
              Saved ${deal.priceDropAmount.toLocaleString()}
            </span>
          )}
        </div>

        {/* Reserved urgency row — always takes up the same space */}
        <div className="mb-4 min-h-[2.25rem] flex items-center">
          {isLastChance && timeLeft ? (
            <div className="flex items-center justify-between w-full bg-red-50 border border-red-100 rounded-lg px-3 py-1.5">
              <span className="text-xs font-bold text-red-600 uppercase tracking-wide">Almost Gone</span>
              <span className="text-xs font-semibold text-red-600 flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true" />
                {timeLeft} left
              </span>
            </div>
          ) : deal.urgencyMessage ? (
            <p className="text-xs text-[#1C2B1E]/60 leading-snug line-clamp-2">{deal.urgencyMessage}</p>
          ) : (
            <span />
          )}
        </div>

        {/* Button pushed to bottom */}
        <Link
          to={`/inventory/${deal.id}`}
          className="mt-auto block w-full text-center bg-[#1C2B1E] hover:bg-[#2C3E55] text-white py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap"
        >
          See This Deal
        </Link>
      </div>
    </div>
  );
}
